import { Component, Input, OnInit } from '@angular/core';
import { TicketAirType } from "@afidium/ts-facade";
import { IGetBookingsCommonResponse } from "libraries/service/resources/booking/interfaces/IGetBookings";
import {
    ComputeValuePipe,
    EConfigurableTableFields,
    IValueType
} from "libraries/pipes/configurable-table/compute-value.pipe";
import { IStatusColorationRule } from "libraries/component/status-tag/status-tag.component";
import { faPlane, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { IProcessReportBookingItem } from "libraries/interfaces/process-report/i-process-report-response";

@Component({
    selector: 'configurable-td-content',
    template: `
        <ng-container *ngIf="configurableTdContent?.value">
            <ng-container [ngSwitch]="configurableTdContent.type">
                <consumed-detail *ngSwitchCase="'consumedDetail'"
                                 [consumedDetail]="configurableTdContent.value">
                </consumed-detail>
                <price-detail *ngSwitchCase="'priceDetail'"
                              [priceDetail]="configurableTdContent.value">
                </price-detail>
                <string-with-label *ngSwitchCase="'stringWithLabel'"
                                   [stringWithLabel]="configurableTdContent.value">
                </string-with-label>
                <text-with-label *ngSwitchCase="'textWithLabel'"
                                 [textWithLabel]="configurableTdContent.value">
                </text-with-label>
                <value-with-name *ngSwitchCase="'valueWithName'"
                                 [valueWithName]="configurableTdContent.value">
                </value-with-name>
                <from-to-hyphen *ngSwitchCase="'fromToHyphen'"
                                [fromToHyphen]="configurableTdContent.value">
                </from-to-hyphen>
                <fa-icon *ngSwitchCase="'bookingType'" [icon]="element | bookingIcon"></fa-icon>
                <external-booking *ngSwitchCase="'externalBooking'"
                                  [externalBookings]="configurableTdContent.value"></external-booking>
                <ng-container *ngSwitchCase="'hostCode'">
                    <ng-container *ngIf="element.syscode; else noSyscodeForLink">
                        <a [routerLink]="['/sasha/booking/'+element.syscode]"
                           class="booking-link">
                            {{configurableTdContent.value}}
                        </a>
                    </ng-container>
                    <ng-template #noSyscodeForLink>
                        {{configurableTdContent.value}}
                    </ng-template>
                </ng-container>
                <ng-container *ngSwitchCase="'processStatus'">
                    <booking-status mode="icon"
                                    class="fake-padding"
                                    [status]="configurableTdContent.value"
                                    [statusColorationRule]="statusColorationRule"></booking-status>
                    <booking-status mode="label"
                                    [status]="configurableTdContent.value"
                                    [label]="null"
                                    [statusColorationRule]="statusColorationRule"></booking-status>
                </ng-container>
                <booking-quality-control-data *ngSwitchCase="'qualityCheckContent'"
                                              [booking]="configurableTdContent.value"></booking-quality-control-data>
                <booking-quality-control-title *ngSwitchCase="'qualityCheckTitle'"
                                               [booking]="configurableTdContent.value"></booking-quality-control-title>
                <booking-sectors *ngSwitchCase="'sectorAir'" [booking]="configurableTdContent.value"></booking-sectors>
                <seat-status-sector *ngSwitchCase="'seatStatusSector'"
                                    [booking]="configurableTdContent.value"></seat-status-sector>
                <booking-status *ngSwitchCase="'xStatus'" mode="icon"
                                [status]="configurableTdContent.value"></booking-status>
                <issue-status *ngSwitchCase="'issueStatus'" [booking]="configurableTdContent.value"></issue-status>
                <ng-container *ngSwitchCase="'xStatusExtended'">
                    <booking-status mode="icon"
                                    [status]="configurableTdContent.value"></booking-status>
                    <booking-status mode="label"
                                    [status]="configurableTdContent.value"
                                    [label]="null"></booking-status>
                </ng-container>
                <traveller-quantity *ngSwitchCase="'travellerQuantity'"
                                    [quantity]="configurableTdContent.value"></traveller-quantity>
                <ng-container *ngSwitchCase="'searchPrice'">
                    <search-form-price [booking]="configurableTdContent.value"></search-form-price>
                </ng-container>
                <ng-container *ngSwitchCase="'fareOptimisationSummary'">
                    <fare-optimisation-summary
                            [fareOptimisation]="configurableTdContent.fareOptimisation"
                            [syscode]="element.syscode"
                            [booking]="configurableTdContent.booking"
                    ></fare-optimisation-summary>
                </ng-container>

                <ng-container *ngSwitchCase="'fareOptimisationList'">
                    <fare-optimisation-list
                            [fareOptimisation]="configurableTdContent.fareOptimisation"
                            [booking]="configurableTdContent.booking"
                            [bookingSyscode]="element.syscode"></fare-optimisation-list>
                </ng-container>
                <ng-container *ngSwitchCase="'interface'">
                    <interface-flag [interface]="configurableTdContent.value"></interface-flag>
                </ng-container>

                <div *ngSwitchCase="'fakePadding'" class="fake-padding">
                </div>
                <ng-container *ngSwitchDefault>{{configurableTdContent.value}}</ng-container>
            </ng-container>
            <ng-container *ngIf="!isLastItemOfGroup && separator">{{separator}}</ng-container>
        </ng-container>
    `,
    styles: [`
        .fake-padding {
            padding: 3px;
            display: inline-block;
        }`]
})
export class ConfigurableTdContentComponent implements OnInit {
    @Input()
    public element: TicketAirType | IProcessReportBookingItem | IGetBookingsCommonResponse;

    @Input()
    public fieldKey: EConfigurableTableFields;

    @Input()
    public isLastItemOfGroup: boolean;

    @Input()
    public separator: string;

    @Input()
    public path: string;

    public plane: IconDefinition = faPlane;
    public statusColorationRule: IStatusColorationRule = {
        green: "OK",
        red: "Danger",
    };
    public configurableTdContent: IValueType;

    constructor(private computeValue: ComputeValuePipe) {
    }

    public ngOnInit(): void {
        this.initConfigurableTdContent();
    }

    private initConfigurableTdContent(): void {
        this.configurableTdContent = this.computeValue.transform(this.element, this.fieldKey, this.path);
    }
}
